import { useId, useState } from 'react';
import {
  Button,
  ErrorState,
  FormField,
  LoadingState,
  Modal,
  Stars,
  StatusPill,
  Textarea,
  type PillTone,
} from '../../components';
import { useSession } from '../../lib/auth/useSession';
import { formatDate, formatDateTime } from '../../lib/format/dates';
import { notify } from '../../services/notify';
import { useDetailLinks } from '../orders/DetailDialogs';
import { SectionLabel } from '../profiles/people';
import type { FeedbackDetail, TriageStatus } from './api';
import { useFeedbackDetail, useTriageFeedback } from './queries';

export const TRIAGE_LABEL: Record<TriageStatus, string> = {
  new: 'New',
  follow_up: 'Needs follow-up',
  reviewed: 'Reviewed',
};
export const TRIAGE_TONE: Record<TriageStatus, PillTone> = { new: 'info', follow_up: 'warning', reviewed: 'success' };

/**
 * Feedback detail (kit `FeedbackDetailModal`, lg), URL-addressable as
 * `?feedback=<id>`. Triage
 * sets the status and appends the optional note to the history.
 */
export function FeedbackDialog({
  id,
  onOpenFeedback,
  onClose,
}: {
  id: string;
  onOpenFeedback: (id: string) => void;
  onClose: () => void;
}) {
  const detail = useFeedbackDetail(id);
  return (
    <Modal isOpen onClose={onClose} size="lg">
      {detail.isPending ? (
        <LoadingState label="Loading feedback…" />
      ) : detail.isError ? (
        <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />
      ) : (
        <FeedbackBody detail={detail.data} onOpenFeedback={onOpenFeedback} onClose={onClose} />
      )}
    </Modal>
  );
}

function FeedbackBody({
  detail,
  onOpenFeedback,
  onClose,
}: {
  detail: FeedbackDetail;
  onOpenFeedback: (id: string) => void;
  onClose: () => void;
}) {
  const noteId = useId();
  const { profile: me, locked } = useSession();
  const lock = locked('crm.write');
  const { openOrder } = useDetailLinks();
  const triage = useTriageFeedback(detail.feedback.id);
  const [note, setNote] = useState('');
  const { feedback: fb, triage: state, chef, eater } = detail;
  const chefName = chef?.displayName || 'this chef';

  const act = (status: TriageStatus, done: string) =>
    triage.mutate(
      { status, note: note.trim() || undefined },
      {
        onSuccess: () => {
          notify.success(done);
          onClose();
        },
      },
    );

  return (
    <div>
      <div className="mb-4 pr-8">
        <div className="mb-2 flex items-center gap-3">
          <Stars n={fb.rating} />
          <StatusPill tone={TRIAGE_TONE[state.status]} dot>
            {TRIAGE_LABEL[state.status]}
          </StatusPill>
        </div>
        <div className="font-heading text-[22px] font-bold tracking-[-.02em] text-brown-deep">
          {detail.listing?.title || 'Untitled dish'}
        </div>
        <div className="mt-[3px] text-[13px] text-muted">
          {eater?.displayName || 'An eater'} → {chef?.displayName || 'a chef'} · {formatDate(fb.createdAt)}
          {detail.order && (
            <>
              {' · '}
              <button
                type="button"
                onClick={() => openOrder(detail.order!.id)}
                className="border-0 bg-transparent p-0 font-mono text-[13px] text-muted underline decoration-dotted underline-offset-2 hover:text-orange"
              >
                {detail.order.orderNumber}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="mb-[18px] rounded-xl bg-cream-warm px-[17px] py-[15px]">
        <p className="m-0 font-serif text-[17px] italic leading-normal text-brown-deep">
          {fb.note ? `“${fb.note}”` : 'No comment left.'}
        </p>
      </div>

      <div className="mb-[18px]">
        <SectionLabel>Prior feedback about {chefName}</SectionLabel>
        {detail.priorForChef.length === 0 ? (
          <div className="text-[13px] text-muted">No other feedback about this chef.</div>
        ) : (
          detail.priorForChef.slice(0, 10).map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => onOpenFeedback(p.id)}
              className="flex w-full items-center gap-[11px] border-0 border-t border-solid border-[rgba(143,68,32,.08)] bg-transparent py-2 text-left font-body text-[13px] hover:bg-[rgba(143,68,32,.04)]"
            >
              <Stars n={p.rating} />
              <span className="min-w-0 flex-1 truncate text-[rgba(42,22,9,.72)]">{p.note || p.dishTitle}</span>
              <span className="shrink-0 text-muted">{formatDate(p.submittedAt)}</span>
            </button>
          ))
        )}
        {detail.priorCount > 10 && (
          <div className="pt-1.5 text-xs text-muted">Showing the latest 10 of {detail.priorCount}.</div>
        )}
      </div>

      {state.notes.length > 0 && (
        <div className="mb-[18px]">
          <SectionLabel>Triage notes</SectionLabel>
          {[...state.notes].reverse().map((n) => (
            <div key={n.id} className="border-t border-[rgba(143,68,32,.08)] py-2 text-sm">
              <div className="mb-0.5 text-xs text-muted">
                {formatDateTime(n.at)}
                {n.byProfileId && ` · ${n.byProfileId === me?.id ? 'you' : n.byProfileId}`}
              </div>
              <div className="whitespace-pre-wrap">{n.text}</div>
            </div>
          ))}
        </div>
      )}

      <FormField label="Triage note" htmlFor={noteId} optional hint="Internal only. Saved with the status change.">
        <Textarea
          id={noteId}
          surface="white"
          rows={2}
          placeholder="What you found or did…"
          value={note}
          disabled={!!lock}
          onChange={(e) => setNote(e.target.value)}
        />
      </FormField>

      <div className="mt-[18px] flex justify-end gap-2 border-t border-[rgba(143,68,32,.1)] pt-4">
        <Button variant="ghost-dark" size="sm" onClick={onClose}>
          Close
        </Button>
        {state.status !== 'new' && (
          <Button
            variant="ghost-dark"
            size="sm"
            loading={triage.isPending}
            lockedReason={lock}
            onClick={() => act('new', 'Reopened as new.')}
          >
            Reopen
          </Button>
        )}
        {state.status !== 'follow_up' && (
          <Button
            variant="ghost-dark"
            size="sm"
            loading={triage.isPending}
            lockedReason={lock}
            onClick={() => act('follow_up', 'Marked as needing follow-up.')}
          >
            Needs follow-up
          </Button>
        )}
        {state.status !== 'reviewed' && (
          <Button
            size="sm"
            loading={triage.isPending}
            lockedReason={lock}
            onClick={() => act('reviewed', 'Marked as reviewed.')}
          >
            Mark reviewed
          </Button>
        )}
      </div>
    </div>
  );
}
